import type { SitePageData } from "../data/pages";
import type { Author } from "../types";
import { Container } from "../components/ui/Container";
import { StarMark } from "../components/ui/StarMark";
import { ContactForm } from "../components/sections/ContactForm";
import { CartografiaPromo, CollaboratorsCarousel } from "../components/sections/CartografiaMedia";
import { ConcursScroll } from "../components/carousel/ConcursScroll";
import { LinkButton } from "../components/ui/Button";
import { ProductActions, ProductPrice } from "../components/ui/AddToCart";
import { concursCards } from "../data";
import "./ContentPage.css";
import {
	assetUrl,
	hasWordPressConfig,
	wooProduct,
	wooShopProducts,
	type WooProductSummary,
} from "../wordpress";

export function ContentPage({ page }: { page: SitePageData }) {
	const product = page.productSlug ? wooProduct(page.productSlug) : null;
	const shopProducts = page.layout === "shop" ? wooShopProducts() : [];
	const useLocalShop = page.layout === "shop" && !hasWordPressConfig();

	return (
		<article
			className={[
				"content-page",
				page.productSlug ? `content-page--${page.productSlug}` : "",
			]
				.filter(Boolean)
				.join(" ")}
		>
			<Container>
				<header className="content-page__header">
					<div>
						<p className="content-page__kicker">{page.kicker}</p>
						<h1>{page.title}</h1>
						{product && (
							<div className="content-page__buy-box">
								<ProductPrice product={product} />
								<ProductActions product={product} />
							</div>
						)}
						<p className="content-page__lead">{page.lead}</p>
					</div>
					<StarMark className="content-page__star" />
				</header>

				{page.image && <img className="content-page__hero" src={assetUrl(page.image)} alt="" />}
				{page.layout === "presentation" && <CartografiaPromo />}

				{page.layout === "shop" ? (
					shopProducts.length > 0 ? (
						<ShopGrid products={shopProducts} />
					) : useLocalShop ? (
						<div className="content-page__body content-page__body--shop">
							{page.sections.map((section, index) => (
								<PageSection
									key={section.title}
									section={section}
									index={index}
									layout={page.layout}
								/>
							))}
						</div>
					) : (
						<p className="content-page__empty" role="status">
							No hi ha llibres disponibles en aquest moment.
						</p>
					)
				) : (
					page.layout !== "contact" && (
						<div className={["content-page__body", page.layout ? `content-page__body--${page.layout}` : ""].filter(Boolean).join(" ")}>
							{page.sections.map((section, index) => (
								<PageSection
									key={section.title}
									section={section}
									index={index}
									layout={page.layout}
								/>
							))}
						</div>
					)
				)}

				{page.layout === "contact" && <ContactForm />}
				{page.layout === "contest" && (
					<ConcursScroll
						cards={concursCards}
						title="Bases, calendari i premis"
						showText
					/>
				)}
				{page.layout === "presentation" && <CollaboratorsCarousel />}
			</Container>
		</article>
	);
}

/**
 * Grade real da loja: todo o catálogo visível do WooCommerce, não uma
 * seleção fixa. Reaproveita a mesma classe visual dos cartões estáticos
 * (`content-page__section--shop`) para não duplicar estilo.
 */
function ShopGrid({ products }: { products: WooProductSummary[] }) {
	return (
		<div className="content-page__body content-page__body--shop">
			{products.map((product) => (
				<article className="content-page__section content-page__section--shop" key={product.id}>
					<a href={product.href}>
						<img
							src={/\/pur-nara-vidal\/?(?:[?#]|$)/.test(product.href)
								? assetUrl("/assets/MOCKUPS_LIVROS/PUR_COSANOVA.png")
								: product.image}
							alt={product.title}
							loading="lazy"
						/>
					</a>
					<div className="content-page__section-copy">
						<h2>
							<a href={product.href}>{product.title}</a>
						</h2>
						{product.shortDescriptionLine && (
							<p className="product-short-description">({product.shortDescriptionLine})</p>
						)}
						<ProductPrice product={product} />
						<ProductActions product={product} />
					</div>
				</article>
			))}
		</div>
	);
}

type PageSectionProps = {
	section: SitePageData["sections"][number];
	index: number;
	layout: SitePageData["layout"];
};

function PageSection({ section, index, layout }: PageSectionProps) {
	const isObjectives =
		layout === "editorial" && section.title === "Els nostres objectius editorials";

	return (
		<section
			className={[
				"content-page__section",
				section.image ? "content-page__section--image" : "",
				layout === "editorial"
					? `content-page__section--editorial content-page__section--editorial-${index + 1}`
					: "",
				layout === "shop" ? "content-page__section--shop" : "",
				layout === "team" ? "content-page__section--team" : "",
			]
				.filter(Boolean)
				.join(" ")}
		>
			{section.image && <img src={assetUrl(section.image)} alt={section.imageAlt ?? ""} />}
			<div className="content-page__section-copy">
				{!section.hideTitle && (
					<h2 className={layout === "editorial" ? "content-page__heading--brand" : undefined}>
						{layout === "editorial" && <StarMark className="content-page__heading-star" />}
						{section.title}
					</h2>
				)}
				{isObjectives ? (
					<ul className="editorial-objectives">
						{section.paragraphs.map((paragraph) => (
							<li key={paragraph}>{renderEmphasis(paragraph)}</li>
						))}
					</ul>
				) : (
					section.paragraphs.map((paragraph) => (
						<p key={paragraph}>{renderEmphasis(paragraph)}</p>
					))
				)}
				{section.author && <AuthorProfile author={section.author} />}
				{section.href && (
					<LinkButton href={section.href} variant="outline">
						{section.ctaLabel ?? "Més informació"}
					</LinkButton>
				)}
			</div>
		</section>
	);
}

/** Só aparece quando o cliente tiver aprovado conteúdo suficiente para o perfil. */
function AuthorProfile({ author }: { author: Author }) {
	if (!author.image && !author.bio && !author.url) return null;
	const content = (
		<>
			{author.image && <img src={assetUrl(author.image)} alt={`Retrat de ${author.name}`} loading="lazy" />}
			<div>
				<p className="author-profile__eyebrow">Autoria</p>
				<h3>{author.name}</h3>
				{author.bio && <p>{author.bio}</p>}
			</div>
		</>
	);

	return author.url ? (
		<a className="author-profile" href={author.url} target="_blank" rel="noopener noreferrer" aria-label={`Obrir pàgina de ${author.name} en una pestanya nova`}>
			{content}
		</a>
	) : (
		<div className="author-profile">{content}</div>
	);
}

/** Marca a negrito os trechos entre `**`, como no Markdown. */
function renderEmphasis(text: string) {
	return text.split("**").map((part, index) =>
		index % 2 === 1 ? (
			// A lista vem sempre da mesma divisão e nunca é reordenada: o índice
			// é a chave estável aqui, e o texto pode repetir-se.
			// eslint-disable-next-line react/no-array-index-key
			<strong key={index}>{part}</strong>
		) : (
			part
		),
	);
}
